import React, { useState } from 'react'
import { useCreateLayoutHost } from '@sketch/utils'
import { LayersTree } from '../LayersTree'
import * as S from './CanvasLeftPanel.styles'
import { useForTablet, ResizeHandle, Tooltip } from '@sketch/components'
import { IS_EMBEDDED } from '@sketch/constants'
import { useCanvasLeftPanelResizing } from './CanvasLeftPanelResizingContext'
import { LayerTreePersistentState } from '../LayersTreePersistentState'
import { useCanvasHost } from '../../host'
import { useCanvasScope } from '../../scope'
import { useGetActiveSidebarTab } from '../../../shares/components/ShareSidebarTabContext'
import { INSPECTOR_SEGMENT } from '../../../shares/constants'
import { canvasLeftPanelToken } from './canvasLeftPanelPortal'

/**
 * Floating panel on the left of the canvas: the entity selector, and the layers
 * tree when the inspector is open.
 *
 * Mounted once beside the canvas, for every view. What it navigates between -
 * pages, sibling Sections, sibling frames - is the only part that differs, and
 * each view puts its own there through `CanvasLeftPanelPortal` rather than
 * being passed down as children, so the panel outlives the view the way the
 * canvas does.
 *
 * Everything else it reads for itself: whether the inspector is open, and the
 * document and page whose collapsed state it should remember. Four views were
 * computing all three identically and threading them down two or three layers
 * to get here.
 *
 * It draws nothing when embedded, where there is no room for it. That was a
 * guard at each call site until the Section view was found to be missing it -
 * it belongs to the panel, not to whoever mounts one.
 */
export function CanvasLeftPanel() {
  const [hideLayersTree, setHideLayersTree] = useState(false)
  const setNavigationHost = useCreateLayoutHost(canvasLeftPanelToken)
  const isTabletAndBigger = useForTablet()

  const isInspectPanelOpen = useGetActiveSidebarTab() === INSPECTOR_SEGMENT

  const host = useCanvasHost()
  const scope = useCanvasScope()
  const stateKey = `${host?.identifier ?? ''}:${scope?.pageUUID ?? ''}`

  /** true if the layers tree feature is enabled, could still be hidden */
  const hasLayersTree = isInspectPanelOpen && isTabletAndBigger

  const { width, isResizing, resizeHandleRef } = useCanvasLeftPanelResizing()

  if (IS_EMBEDDED) {
    return null
  }

  return (
    <S.CanvasLeftPanel
      style={hasLayersTree ? { width } : undefined}
      $isResizing={isResizing}
      data-testid="canvas-left-panel"
    >
      {hasLayersTree && <ResizeHandle ref={resizeHandleRef} />}
      <S.CanvasLeftPanelHeader $isResizing={isResizing}>
        <S.SelectorContainer ref={setNavigationHost} />
        {hasLayersTree && (
          <S.ToggleLayersTreeContainer>
            <S.ToggleLayersTreeButtonDivider />
            <Tooltip
              content={hideLayersTree ? 'Show Layer List' : 'Hide Layer List'}
              placement="bottom"
              disableWhenTouchDevice
            >
              <S.ToggleLayersTreeButton
                $isActive={!hideLayersTree}
                onClick={() => setHideLayersTree(prev => !prev)}
                aria-label={
                  hideLayersTree ? 'Show Layer List' : 'Hide Layer List'
                }
              >
                <S.ToggleLayersTreeButtonIcon />
              </S.ToggleLayersTreeButton>
            </Tooltip>
          </S.ToggleLayersTreeContainer>
        )}
      </S.CanvasLeftPanelHeader>
      <LayerTreePersistentState collapsedStateKey={stateKey}>
        {hasLayersTree && !hideLayersTree && (
          <S.LayersTreeContainer $isResizing={isResizing}>
            <LayersTree />
          </S.LayersTreeContainer>
        )}
      </LayerTreePersistentState>
    </S.CanvasLeftPanel>
  )
}
